import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, ChevronLeft, ChevronRight, ChevronDown, Star } from "lucide-react";
import { ProductVisual } from "./ProductVisual";
import { useCart } from "../context/CartContext";
import { productSubcategories } from "../lib/product";
import { toast } from "sonner";
import { PRODUCTS as P_TID } from "../constants/testIds";
import { resolvePhotoUrl } from "../lib/photos";

export { resolvePhotoUrl };

// Distance (px) a drag/swipe must cover to change the photo on release.
const SWIPE_THRESHOLD = 40;

const ProductPhotos = ({ photos, name, kind, onPhotoClick }) => {
    const urls = (photos || []).map(resolvePhotoUrl).filter(Boolean);
    const [index, setIndex] = useState(0);
    const [dragOffset, setDragOffset] = useState(0);
    const gesture = useRef({ active: false, startX: 0, moved: false });

    const count = urls.length;
    const current = Math.min(index, count - 1);
    const go = (dir) => setIndex((i) => Math.max(0, Math.min(count - 1, i + dir)));

    // Pointer events cover both mouse drag and touch swipe. The track is moved
    // by transform — no native scrolling involved — so behaviour is identical
    // on desktop and mobile.
    const onPointerDown = (e) => {
        // Buttons (arrows, dots) keep their own clicks: capturing the pointer
        // when the press starts on one would retarget its click to this
        // container, so the button's handler would never run. The gesture
        // state is still reset on every press, otherwise a stale "moved" flag
        // (left over from a swipe that never produced a click, e.g. one the
        // browser turned into a page scroll) would swallow the next click.
        const onButton = !!e.target.closest("button");
        gesture.current = { active: !onButton, startX: e.clientX, moved: false };
        if (!onButton) e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => {
        const g = gesture.current;
        if (!g.active) return;
        const dx = e.clientX - g.startX;
        if (Math.abs(dx) > 8) g.moved = true;
        if (g.moved) setDragOffset(dx);
    };
    const endGesture = (e, cancelled) => {
        const g = gesture.current;
        if (!g.active) return;
        g.active = false;
        const dx = cancelled ? 0 : e.clientX - g.startX;
        setDragOffset(0);
        if (!cancelled && g.moved) {
            if (dx <= -SWIPE_THRESHOLD && current < count - 1) go(1);
            else if (dx >= SWIPE_THRESHOLD && current > 0) go(-1);
        }
    };
    // After a real drag/swipe, swallow the click so the tap-to-open handler
    // doesn't fire (mobile browsers synthesize a click after a touch swipe).
    const onClickCapture = (e) => {
        if (gesture.current.moved) {
            e.preventDefault();
            e.stopPropagation();
            gesture.current.moved = false;
        }
    };

    if (count === 0) {
        return <ProductVisual kind={kind} />;
    }

    if (count === 1) {
        return (
            <img
                src={urls[0]}
                alt={name}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
            />
        );
    }

    return (
        <div
            className="relative z-[6] w-full h-full overflow-hidden select-none"
            style={{ touchAction: "pan-y" }}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={(e) => endGesture(e, false)}
            onPointerCancel={(e) => endGesture(e, true)}
            onClickCapture={onClickCapture}
            onClick={(e) => {
                e.stopPropagation();
                // Arrows / dots handle their own clicks — never navigate.
                if (e.target.closest("button")) return;
                if (gesture.current.moved) return;
                onPhotoClick?.();
            }}
        >
            <div
                className="flex h-full w-full"
                style={{
                    transform: `translateX(calc(${-current * 100}% + ${dragOffset}px))`,
                    transition: dragOffset !== 0 ? "none" : "transform 0.3s ease-out",
                }}
            >
                {urls.map((u, i) => (
                    <div key={u + i} className="h-full w-full flex-shrink-0">
                        <img
                            src={u}
                            alt={`${name} ${i + 1}`}
                            loading="lazy"
                            decoding="async"
                            draggable={false}
                            className="pointer-events-none h-full w-full object-cover"
                        />
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    go(-1);
                }}
                disabled={current === 0}
                className="absolute left-1.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors z-10 disabled:opacity-0"
                aria-label="Photo précédente"
            >
                <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    go(1);
                }}
                disabled={current === count - 1}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white transition-colors z-10 disabled:opacity-0"
                aria-label="Photo suivante"
            >
                <ChevronRight className="w-3.5 h-3.5" />
            </button>

            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10">
                {urls.map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIndex(i);
                        }}
                        className={`w-1.5 h-1.5 rounded-full transition-colors ${
                            i === current ? "bg-[#d4af37]" : "bg-neutral-300"
                        }`}
                        aria-label={`Voir la photo ${i + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

const ProductCard = ({ product, index = 0 }) => {
    const [qty, setQty] = useState(1);
    const [expanded, setExpanded] = useState(false);
    const { addItem, setOpen } = useCart();
    const navigate = useNavigate();
    const models = productSubcategories(product);
    const hasGallery = (product.photos || []).length > 1;

    // The gallery sits above the stretched link, so a plain tap opens the product.
    const handlePhotoTap = () => navigate(`/produit/${product.id}`);

    const inc = () => setQty((v) => Math.min(99, v + 1));
    const dec = () => setQty((v) => Math.max(1, v - 1));

    const handleAdd = () => {
        if (models.length > 0) {
            // A model must be chosen — that happens on the product page.
            navigate(`/produit/${product.id}`);
            return;
        }
        addItem(product, qty);
        toast.success(`${qty} × ${product.name} ajouté au panier`, {
            action: {
                label: "Voir panier",
                onClick: () => setOpen(true),
            },
        });
    };

    return (
        <motion.article
            data-testid={P_TID.card(product.id)}
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "100px" }}
            transition={{ duration: 0.35, delay: Math.min(index * 0.02, 0.12) }}
            className="group relative h-full flex flex-col transition-transform duration-500 hover:-translate-y-0.5"
        >
            {/* Visual */}
            <div
                className={`relative w-full aspect-[4/5] sm:aspect-[3/4] rounded-xl sm:rounded-2xl bg-[#F5F5F7] overflow-hidden ${
                    hasGallery ? "cursor-grab active:cursor-grabbing" : ""
                }`}
            >
                <div className="relative w-full h-full group-hover:scale-105 transition-transform duration-700">
                    <ProductPhotos
                        photos={product.photos}
                        name={product.name}
                        kind={product.kind}
                        onPhotoClick={handlePhotoTap}
                    />
                </div>
                {product.top_selling && (
                    <span className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-mono-tech tracking-[0.12em] uppercase text-white bg-gradient-to-r from-[#5b21b6] to-[#d4af37] shadow-md">
                        <Star className="w-3 h-3 fill-white" />
                        Top vente
                    </span>
                )}
                {/* Stretched link: whole visual area opens the product page */}
                <Link
                    to={`/produit/${product.id}`}
                    aria-label={`Voir le produit ${product.name}`}
                    className="absolute inset-0 z-[5] rounded-xl sm:rounded-2xl"
                />
            </div>

            {/* Content */}
            <div className="pt-3 sm:pt-4 px-1 flex flex-col flex-1">
                <h3 className="font-display text-[14px] sm:text-[18px] tracking-tight font-medium text-neutral-900 leading-tight">
                    <Link
                        to={`/produit/${product.id}`}
                        className="hover:text-[#7c3aed] transition-colors"
                    >
                        {product.name}
                    </Link>
                </h3>
                <p className="mt-1.5 text-[13px] text-neutral-500">{product.tagline}</p>

                <ul className="mt-3.5 space-y-1.5 text-[12px] text-neutral-600 font-mono-tech">
                    {(expanded ? product.specs || [] : (product.specs || []).slice(0, 3)).map((s) => (
                        <li key={s} className="flex items-center gap-2">
                            <span
                                className="w-1 h-1 rounded-full shrink-0"
                                style={{ background: product.accent }}
                            />
                            <span className={`min-w-0 ${expanded ? "" : "truncate"}`} title={s}>
                                {s}
                            </span>
                        </li>
                    ))}
                </ul>

                {((product.specs || []).length > 3 || (product.specs || []).some((s) => s.length > 30)) && (
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="mt-3 inline-flex items-center gap-1 text-[12px] font-medium text-[#d4af37] hover:text-[#7c3aed] transition-colors"
                    >
                        {expanded ? "Réduire" : "Lire la suite"}
                        <ChevronDown
                            className={`w-3.5 h-3.5 transition-transform ${
                                expanded ? "rotate-180" : ""
                            }`}
                        />
                    </button>
                )}

                <div className="mt-auto pt-5 flex items-end justify-between gap-3">
                    {/* Qty selector */}
                    <div
                        className="flex items-center gap-1 border border-black/10 rounded-full overflow-hidden"
                        data-testid={`product-qty-${product.id}`}
                    >
                        <button
                            type="button"
                            onClick={dec}
                            aria-label="Diminuer la quantité"
                            data-testid={`product-qty-minus-${product.id}`}
                            className="w-8 h-9 flex items-center justify-center hover:bg-black/[0.04] transition"
                        >
                            <Minus className="w-3.5 h-3.5" />
                        </button>
                        <span
                            className="min-w-[20px] text-center text-[13px] font-medium tabular-nums"
                            data-testid={`product-qty-value-${product.id}`}
                        >
                            {qty}
                        </span>
                        <button
                            type="button"
                            onClick={inc}
                            aria-label="Augmenter la quantité"
                            data-testid={`product-qty-plus-${product.id}`}
                            className="w-8 h-9 flex items-center justify-center hover:bg-black/[0.04] transition"
                        >
                            <Plus className="w-3.5 h-3.5" />
                        </button>
                    </div>

                    {/* Price + Add to cart */}
                    <div className="flex flex-col items-end gap-1.5 flex-1 min-w-0">
                        <div className="flex items-center gap-2 mr-2 mb-1">
                            {product.original_price && (
                                <span className="text-[11px] text-neutral-400 line-through tabular-nums whitespace-nowrap">
                                    {product.original_price} DH / د.م.
                                </span>
                            )}
                            <span className="text-[13px] font-bold text-neutral-900 tabular-nums whitespace-nowrap">
                                {product.price_from && product.price_from !== "Sur devis"
                                    ? `${product.price_from} DH / د.م.`
                                    : "Sur devis"}
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={handleAdd}
                            data-testid={`product-add-${product.id}`}
                            aria-label="Ajouter au panier"
                            className="group/bag relative inline-flex h-11 w-12 items-center justify-center rounded-xl border-2 border-black bg-transparent text-black transition-colors hover:bg-emerald-600 hover:border-emerald-600 hover:text-white"
                        >
                            <ShoppingBag className="w-5 h-5" strokeWidth={1.8} />
                            <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-black text-white ring-2 ring-white">
                                <Plus className="w-2.5 h-2.5" strokeWidth={3.5} />
                            </span>
                            <span className="sr-only">Ajouter au panier</span>
                        </button>
                    </div>
                </div>
            </div>
        </motion.article>
    );
};

export default ProductCard;
